import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SeoSocial } from "@/components/SeoSocial";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { PortfolioFooter, PortfolioHeader } from "./PortfolioLayout";
import { PortfolioJournal, PortfolioProjects } from "./PortfolioSections";
import { PORTFOLIO_REPERES } from "./portfolio-content";
import "./portfolio.css";
import "./portfolio-atelier.css";

function BuildDiagram() {
  return (
    <svg className="pf-build-diagram" viewBox="0 0 420 490" role="img" aria-labelledby="build-diagram-title">
      <title id="build-diagram-title">De la conception au code, puis au produit</title>
      <g className="pf-diagram-guides" fill="none">
        <path d="M68 15v415M190 0v290M320 65v390M355 310v175M45 80l330 185M45 190l330 185M80 335l290-163M112 455l270-154" />
        <path d="M62 30h12m-6-6v12M349 470h12m-6-6v12M184 15h12m-6-6v12" />
      </g>
      <g className="pf-diagram-boxes" fill="none">
        <path d="M70 90 190 25 310 90 190 155Z M70 90V124L190 189 310 124V90 M190 155V189" />
        <path d="M112 239 222 179 332 239 222 299Z M112 239V273L222 333 332 273V239 M222 299V333" />
        <path d="M158 391 258 336 358 391 258 446Z M158 391V425L258 480 358 425V391 M258 446V480" />
      </g>
      <g className="pf-diagram-connectors" fill="none"><path d="M90 135v69l42 23" /><path d="M132 284v68l46 25" /></g>
      <g className="pf-diagram-labels" textAnchor="middle"><text x="190" y="96">CONCEPTION</text><text x="222" y="245">CODE</text><text x="258" y="397">PRODUIT</text></g>
    </svg>
  );
}

export function PortfolioHome() {
  return (
    <div className="portfolio-home pf-atelier-home" id="page-top">
      <SeoSocial title="Tokam Darius | Développement web, IA & automatisation" description="Je conçois et développe des applications web et des outils d'automatisation. Découvrez mes projets et ma façon de travailler. Basé à Jonquière, au Québec." url="https://tokamdarius.ca/" />
      <Helmet>
        <title>Tokam Darius | Développement web, IA & automatisation</title>
        <meta name="description" content="Je conçois et développe des applications web et des outils d'automatisation. Découvrez mes projets et ma façon de travailler. Basé à Jonquière, au Québec." />
        <link rel="canonical" href="https://tokamdarius.ca/" />
        <link rel="preload" href="/fonts/anton-v1.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href="/fonts/manrope-regular-v1.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href="/fonts/manrope-semibold-v1.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="preload" href="/portfolio/portrait-darius-hero-640-v1.webp" as="image" type="image/webp" media="(max-width: 700px)" />
        <link rel="preload" href="/portfolio/portrait-darius-hero-v2.webp" as="image" type="image/webp" media="(min-width: 701px)" />
      </Helmet>
      <a href="#portfolio-content" className="pf-skip-link">Aller au contenu</a>
      <main id="portfolio-content" tabIndex={-1}>
        <section id="accueil" className="pf-hero" aria-labelledby="hero-title">
          <PortfolioHeader />
          <div className="pf-hero-copy">
            <h1 id="hero-title"><span>TOKAM</span><span>DARIUS</span></h1>
            <p className="pf-hero-specialty">DIGITAL SYSTEMS</p>
            <Link to="/projets" className="pf-button">Voir mes projets <ArrowRight size={22} strokeWidth={1.5} /></Link>
          </div>
          <div className="pf-vermilion" aria-hidden="true" />
          <picture>
            <source media="(max-width: 700px)" srcSet="/portfolio/portrait-darius-hero-420-v1.webp 420w, /portfolio/portrait-darius-hero-640-v1.webp 640w" sizes="100vw" />
            <img className="pf-hero-portrait" src="/portfolio/portrait-darius-hero-v2.webp" alt="Portrait de Tokam Darius, développeur web" width={1122} height={1402} loading="eager" {...{ fetchpriority: "high" }} decoding="async" />
          </picture>
          <p className="pf-hero-aside">Du code.<br />Des idées.<br />De l'impact.</p>
          <div className="pf-hero-bottom"><p>Développeur web & IA<br />Jonquière, Québec</p><Link to="/projets" aria-label="Explorer les réalisations"><span>Explorer</span><ArrowDown size={18} /></Link></div>
        </section>

        <div className="pf-atelier">
          <section className="pf-atelier-section pf-atelier-intro" aria-labelledby="intro-title">
            <div className="pf-atelier-index"><span>01</span><span>Idées <ArrowRight size={12} aria-hidden="true" /> Solutions <ArrowRight size={12} aria-hidden="true" /> Impact</span><span>Développement web<br />Automatisation · IA</span></div>
            <div className="pf-atelier-intro-grid">
              <div><h2 id="intro-title">Vous avez<br />une idée.<span>Je lui donne vie.</span></h2><p>Applications web, outils connectés<br />et automatisation.</p></div>
              <BuildDiagram />
            </div>
            <dl className="pf-atelier-reperes">{PORTFOLIO_REPERES.map(stat => <div key={stat.label}><dd>{stat.value}</dd><dt>{stat.label}</dt><dd>{stat.detail}</dd></div>)}</dl>
          </section>
          <PortfolioProjects preview />
          <section className="pf-atelier-section pf-atelier-navigation" aria-label="Explorer les services et le parcours">
            <div className="pf-atelier-index"><span>03</span><span>Navigation rapide</span></div>
            <div className="pf-atelier-routes">
              <Link to="/services" className="pf-atelier-route"><div><h2>Les services <ArrowUpRight strokeWidth={1} aria-hidden="true" /></h2><span className="pf-atelier-link">Explorer les services <ArrowRight size={19} aria-hidden="true" /></span></div><ul><li>Sites web</li><li>Outils sur mesure</li><li>Automatisation</li><li>Intégrations IA</li></ul></Link>
              <Link to="/a-propos" className="pf-atelier-route"><div><h2>À propos <ArrowUpRight strokeWidth={1} aria-hidden="true" /></h2><span className="pf-atelier-link">Faire connaissance <ArrowRight size={19} aria-hidden="true" /></span></div><ul><li>Parcours</li><li>Approche</li><li>Compétences</li><li>Collaborations</li></ul></Link>
            </div>
          </section>
          <PortfolioJournal atelier />
          <section className="pf-atelier-section pf-atelier-contact" aria-labelledby="home-contact-title">
            <div className="pf-atelier-index"><span>05</span><span>Contact</span></div>
            <div className="pf-atelier-contact-grid"><h2 id="home-contact-title">Un projet en tête ?<span>Parlons-en.</span></h2><svg className="pf-atelier-contact-arrow" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 3h16v16h-4V9.83L5.4 21.43 2.57 18.6 14.17 7H5Z" /></svg><div><p>Un besoin, un outil à faire évoluer<br />ou une collaboration à proposer.</p><Link className="pf-button" to="/contact">Me contacter <ArrowRight size={18} aria-hidden="true" /></Link></div></div>
          </section>
        </div>
      </main>
      <PortfolioFooter />
    </div>
  );
}
